// Import necessary dependencies and components
import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import SearchBar from './SearchBar';
import TopicList from './TopicList';
import CurrentBasketInfo from './CurrentBasketInfo';
import { useTopicSelection } from './useTopicSelection';
import { X, Plus } from 'lucide-react'; // Added Plus icon
import AddItemModal from '../AddItemModal/AddItemModal'; // Add this import
import OfflineStatusBanner from '../OfflineStatusBanner/OfflineStatusBanner'; // Add this import

// Main component for topic selection modal
const TopicSelectionModal = ({ 
  onSelect, 
  onAddNew, 
  onClose, 
  currentBasket, 
  currentWorkspaceId 
}) => {
  const [error, setError] = useState(null);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false); // Add this state
  
  // Get all topics for the current workspace from Redux store
  const allWorkspaceTopics = useSelector(state => {
    const topics = state.workspaces.currentWorkspace.topics;
    const baskets = state.workspaces.currentWorkspace.baskets;
    return topics.map(topic => ({
      ...topic,
      basket: topic.basket_id ? { 
        id: topic.basket_id, 
        name: baskets.find(b => b.id === topic.basket_id)?.name 
      } : null
    }));
  });

  // Use custom hook for topic selection logic
  const {
    availableTopics,
    selectedTopics,
    searchTerm,
    handleSearch,
    handleTopicToggle,
    clearSearch,
  } = useTopicSelection(currentWorkspaceId, currentBasket.id, allWorkspaceTopics, currentBasket.topics);


  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#000000';
    hexColor = hexColor.replace('#', '');
    const r = parseInt(hexColor.substr(0, 2), 16);
    const g = parseInt(hexColor.substr(2, 2), 16);
    const b = parseInt(hexColor.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // Handle AddItemModal open/close
  const handleOpenAddItemModal = () => {
    setIsAddItemModalOpen(true);
  };

  const handleCloseAddItemModal = () => {
    setIsAddItemModalOpen(false);
  };

  // Handle new topic creation
  const handleAddItem = async (newTopicData) => {
    try {
      // Properly structure the data for addTopic
      const topicData = {
        workspaceId: currentWorkspaceId,
        newTopic: {
          ...newTopicData,
          workspace_id: currentWorkspaceId
        }
      };

      await onAddNew(topicData);
      handleCloseAddItemModal();
      onClose(); // Close the TopicSelectionModal after successful creation
    } catch (error) {
      console.error('Error adding new item:', error);
      setError('Failed to create new item');
    }
  };

  // Handle adding existing topics
  const handleAddAndClose = useCallback(async () => {
    const topicsToAdd = availableTopics.filter(topic => selectedTopics.includes(topic.id));
    
    if (topicsToAdd.length > 0) {
      try {
        await onSelect(topicsToAdd);
        onClose();
      } catch (error) {
        console.error('Error adding topics:', error);
        setError('Failed to add topics. Please try again.');
      }
    } else {
      setError('No topics selected to add');
    }
  }, [availableTopics, selectedTopics, onSelect, onClose]);

  const highlightText = useCallback((text, term) => {
    if (!text || typeof text !== 'string') return text;
    if (!term) return text;
    const parts = text.split(new RegExp(`(${term})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => 
          part.toLowerCase() === term.toLowerCase() ? 
            <mark key={i} className="bg-yellow-200 rounded">{part}</mark> : part
        )}
      </span>
    );
  }, []);

  // Render the modal
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-7xl h-[95vh] flex flex-col relative overflow-hidden">
        {/* Close button - made more prominent */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-neutral-900 hover:text-neutral-600 transition-colors"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        {/* Add OfflineStatusBanner here */}
        <OfflineStatusBanner />

        {/* Modal header - neutral background */}
        <div className="px-4 py-3 bg-cranberry-50 border-b border-neutral-200">
          <h2 className="text-lg font-bold text-cranberry-700">Add Items to Basket</h2>
        </div>

        {/* Current basket info and Selected Topics - white background */}
        <CurrentBasketInfo basket={currentBasket} />

        <div className="px-4 py-2 bg-white">
          <p className="text-sm text-neutral-600">
            Selected Topics: 
            {selectedTopics.length > 0 ? (
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedTopics.map(topicId => {
                  const topic = allWorkspaceTopics.find(t => t.id === topicId);
                  return topic ? (
                    <span
                      key={topic.id}
                      className="px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center border border-neutral-200"
                      style={{
                        backgroundColor: topic.tag?.color || '#E6E6FF',
                        color: getContrastColor(topic.tag?.color || '#E6E6FF')
                      }}
                    >
                      {topic.name}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTopicToggle(topic.id);
                        }}
                        className="ml-1 hover:text-opacity-75"
                        aria-label={`Remove ${topic.name}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            ) : (
              <span className="ml-1 text-neutral-500">None</span>
            )}
          </p>
        </div>

        {/* Search bar and Create Item button - remove bottom border, add border to create button */}
        <div className="px-4 py-3 flex flex-col sm:flex-row gap-2">
          <div className="flex-grow">
            <SearchBar 
              value={searchTerm} 
              onChange={handleSearch} 
              onClear={clearSearch}
            />
          </div>
          <button
            onClick={handleOpenAddItemModal}
            className="p-2 bg-cranberry-700 text-white rounded-lg hover:bg-cranberry-600 transition-colors flex items-center justify-center border-2 border-neutral-900"
            aria-label="Create Item"
          >
            <Plus size={20} />
          </button>
        </div>

        {/* Topic count section - add border below */}
        <div className="px-4 py-2 bg-white border-b border-neutral-200">
          {availableTopics.length > 0 ? (
            <p className="text-sm text-neutral-600 italic">
              {searchTerm === '' 
                ? `${availableTopics.length} total topic${availableTopics.length === 1 ? '' : 's'}`
                : `${availableTopics.length} matching topic${availableTopics.length === 1 ? '' : 's'} for "${searchTerm}"`
              }
            </p>
          ) : (
            <p className="text-sm text-neutral-600 italic">
              {searchTerm === '' 
                ? "No topics available to add to this basket."
                : `No topics found matching "${searchTerm}".`
              }
            </p>
          )}
        </div>

        {/* List of topics, "No topics" message, or Create New Item placeholder */}
        <div className="flex-grow overflow-y-auto px-2 sm:px-4">
          {availableTopics.length > 0 ? (
            <TopicList
              topics={availableTopics}
              selectedTopics={selectedTopics}
              onTopicToggle={handleTopicToggle}
              searchTerm={searchTerm}
              highlightText={highlightText}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <p className="text-neutral-600 mb-2">
                  {searchTerm === '' 
                    ? "There are no items available to add to this basket."
                    : `No items found matching "${searchTerm}".`
                  }
                </p>
                {searchTerm === '' && (
                  <p className="text-sm text-neutral-400">
                    Try creating new items.
                  </p>
                )}
                {searchTerm !== '' && (
                  <button
                    onClick={clearSearch}
                    className="text-cranberry-700 hover:text-cranberry-600 transition-colors"
                  >
                    Clear search
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer button */}
        <div className="p-4 border-t border-neutral-200 bg-white">
          <button
            onClick={handleAddAndClose}
            className="w-full px-4 py-2 bg-cranberry-700 text-white rounded-lg font-medium hover:bg-cranberry-600 transition-colors disabled:bg-neutral-300 disabled:cursor-not-allowed"
            disabled={selectedTopics.length === 0}
          >
            Add Selected Items ({selectedTopics.length})
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="px-4 py-2 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Add the AddItemModal */}
        {isAddItemModalOpen && (
          <AddItemModal
            isOpen={isAddItemModalOpen}
            onClose={handleCloseAddItemModal}
            onAdd={handleAddItem}
            workspaceId={currentWorkspaceId}
            defaultBasketId={currentBasket.id}
          />
        )}
      </div>
    </div>
  );
};

export default TopicSelectionModal;
